/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/
import * as _ from 'lodash';

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { AllocationStation } from '../../model/allocation-station.model';
import { AppUtilityService } from '../../services/app-utility.service';
import { ConfigService } from '../../services/config.service';
import { DataStoreService } from '../../services/datastore.service';
import { LmsEventDetails } from '../../model/lms-event-details.model';
import { MenuItem } from '../../model/menu-item.model';
import { MessageDetails } from '../../model/message-details.model';
import { MessageHandlerService } from '../../services/message-handler.service';
import { PopupService } from '../../../core/services/popup.service';
import { Router } from '@angular/router';
import { SelectItem } from 'primeng/api/selectitem';
import { StaticDataStoreService } from './../../services/static-datastore.service';
import { Tab } from '../../model/tab.model';
import { environment } from './../../../../environments/environment';

/**
 * This submenu item component created for each side navigation menu item,
 * it will show panel which contains all tabs for particular type.
 * @export
 * @class LmsSubmenuItemComponent
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-submenu-item',
  templateUrl: './lms-submenu-item.component.html',
  styleUrls: ['./lms-submenu-item.component.scss']
})

export class LmsSubmenuItemComponent implements OnInit, OnChanges, OnDestroy {

  @Input() menuItem: MenuItem;

  @Input() tabs: Tab[];

  @Input() tabsIndex: any;

  @Output() lmsTabChange = new EventEmitter<LmsEventDetails>();
  @Input() editMode: Boolean;
  filteredTabs: Tab[];

  selectedTab: Tab;

  selectedSubType: string;

  searchText: string;

  lastSelectTab: Tab;

  subTypes: SelectItem[];

  private defaultLocoInitial = 'CSXT';

  selectedDivisionName: string;

  selectedStationName: string;

  divisionList: Array<SelectItem> = [];

  stationList: Array<SelectItem> = [];

  commonopsPicklist: any;

  allocationStationArr: Array<AllocationStation> = [];

  /**
   * Creates an instance of LmsSubmenuItemComponent.
   * @param {ConfigService} configService
   * @param {DataStoreService} dataStoreService
   * @param {Router} router
   * @memberOf LmsSubmenuItemComponent
   */
  constructor(private configService: ConfigService,
    private dataStoreService: DataStoreService,
    private router: Router,
    private staticDataStoreService: StaticDataStoreService,
    private messageHandlerService: MessageHandlerService, public appUtilityService: AppUtilityService,
    private popupServices: PopupService) { }

  /**
   * This invoked when component is load.
   * @memberOf LmsMenuItemComponent
   */
  ngOnInit() {
    this.init();
  }

  /**
  * This method invoked when any change is done in tabs.
  * @param {SimpleChanges} changes
  * @memberOf LmsTabsComponent
  */
  ngOnChanges(changes: SimpleChanges) {
    const tabChanged: boolean = changes['tabs'] && (changes['tabs'].currentValue !== changes['tabs'].previousValue);
    if (tabChanged) {
      this.init();
    }
  }

  /**
   * initialize selected tab and last selected tab
   * and sub type.
   * @private
   * @memberOf LmsSubmenuItemComponent
   */
  private init() {
    if (this.menuItem && this.menuItem.subTypes) {
      this.subTypes = [];
      for (const subType of this.menuItem.subTypes) {
        // REMOVE CONDITIONS FOR OTHER SUB-TYPES
        this.subTypes.push({
          label: subType,
          value: subType
        });
      }
      this.selectedSubType = this.menuItem.subTypes[0];
    }
    if (this.tabs && this.tabs.length > 0) {
      this.selectedDivisionName = '';
      this.selectedStationName = '';
      this.changeDivision();
      if (this.menuItem && this.menuItem.type === 'Yard-Local') {
        this.divisionListFocus();
      }
      this.filteredTabs = this.tabs.filter((data) => { return this.menuItem.type === data.type; });
      this.selectedTab = this.tabs.find((data) => { return data.selected; });
      if (this.selectedTab) {
        const element = document.getElementById(`leftTab${this.selectedTab.id}`);
        if (element) {
          element.focus();
        }
        this.lastSelectTab = this.selectedTab.type === this.menuItem.type ? this.selectedTab : this.lastSelectTab;
        if (this.lastSelectTab) {
          this.lmsTabChange.emit(<LmsEventDetails>({ type: 'lastSelectTabChange', data: this.lastSelectTab }));
        }
      }
      if (!this.lastSelectTab && this.filteredTabs && this.filteredTabs.length > 0) {
        this.lastSelectTab = this.filteredTabs[0];
      }
    }
  }

  /**
   * Add new tab for selected subtype and search text.
   * @memberOf LmsSubmenuItemComponent
   */
  addTab(isYardLocalDivisionSelected?: boolean) {
    const userProfile = this.staticDataStoreService.getValue('userProfile');
    const managerDesk = this.staticDataStoreService.getValue('commonopsPicklist');
    if (this.filteredTabs.length >= 20) {
      this.messageHandlerService.handleMessage(<MessageDetails>({
        type: this.configService.getConfigData().message.type.client,
        level: this.configService.getConfigData().message.level.error,
        target: this.configService.getConfigData().message.target.page,
        message: this.configService.getConfigData().messages.maxTab
      }));
    } else {
      const tab = <Tab>({
        type: this.menuItem.type,
        subType: this.selectedSubType
      });
      tab.tabSearchParameter = {};
      if (this.menuItem.type === 'Outlying-Points' && !this.searchText) {
        let isDeskPresent = false;
        if (managerDesk && managerDesk.masterData) {
          for (let i = 0; i < managerDesk.masterData.zoneList.length; i++) {
            if (userProfile.alertDesk && userProfile.alertDesk.trim().toLowerCase() === managerDesk.masterData.zoneList[i].zoneCode.trim().toLowerCase()) {
              isDeskPresent = true;
              break;
            }
          }
        }
        if (isDeskPresent) {
          this.searchText = userProfile.alertDesk;
        }
      }
      if (this.searchText) {
        this.searchText = this.searchText.trim();
        if (this.menuItem.type === 'Terminal') {
          tab.tabSearchParameter.searchText = this.getConvertedValue(this.searchText);
        } else if (this.menuItem.type === 'Locomotive') {
          this.searchText = this.formatLocomotiveId(this.searchText);
          tab.tabSearchParameter.searchText = this.searchText;
        } else {
          tab.tabSearchParameter.searchText = this.searchText;
        }
      }
      if (this.menuItem && this.menuItem.type === 'Yard-Local' && this.selectedDivisionName && this.selectedStationName) {
        if (isYardLocalDivisionSelected) {
          this.router.navigateByUrl(`${tab.type.toLowerCase()}/${tab.subType.toLowerCase()}/${new Date().getTime()}?division=${this.selectedDivisionName}&allocationStation=${this.selectedStationName}&fromLeftMenu=true`,
            { skipLocationChange: environment.hideUrl });
        } else {
          this.router.navigateByUrl(`${tab.type.toLowerCase()}/${tab.subType.toLowerCase()}/${new Date().getTime()}?division=${this.selectedDivisionName}&allocationStation=${this.selectedStationName}`,
            { skipLocationChange: environment.hideUrl });
        }
      } else if (this.searchText) {
        this.router.navigateByUrl(`${tab.type.toLowerCase()}/${tab.subType.toLowerCase()}/${new Date().getTime()}?search=${this.searchText}`,
          { skipLocationChange: environment.hideUrl });
      } else {
        this.router.navigateByUrl(`${tab.type.toLowerCase()}/${tab.subType.toLowerCase()}/${new Date().getTime()}`,
          { skipLocationChange: environment.hideUrl });
      }
      this.searchText = null;
      this.hideSideNavSubMenuPanel();
    }
  }

  private hideSideNavSubMenuPanel() {
    if (this.menuItem && this.menuItem.type) {
      const sideSubMenu = document.getElementById('sideOverlay' + this.menuItem.type);
      if (sideSubMenu) {
        sideSubMenu.style.display = 'none';
        sideSubMenu.style.opacity = '0';
      }
      setTimeout(() => {
        sideSubMenu.removeAttribute('style');
      }, 500);
      setTimeout(() => {
        this.selectedSubType = null;
        setTimeout(() => {
          this.selectedSubType = this.subTypes[0].value;
          this.popupServices.setSubmitBtnId('');
          this.popupServices.setPageMode('Read');
        }, 50);
      }, 50);
    }
  }

  /**
   * remove tab from tabstore and corresponding data from datastore.
   * @param {Tab} tab
   * @param {any} $event
   * @memberOf LmsSubmenuItemComponent
   */
  removeTab(tab: Tab, $event) {
    this.dataStoreService.removeTab(tab);
    if (tab.type === this.selectedTab.type) {
      if (this.filteredTabs.length === 1) {
        this.selectedSubType = tab.subType;
        this.addTab();
      } else if (tab.selected) {
        const tabDetails = (this.filteredTabs.indexOf(tab) === 0)
          ? this.filteredTabs[this.filteredTabs.indexOf(tab) + 1] : this.filteredTabs[this.filteredTabs.indexOf(tab) - 1];
        this.router.navigateByUrl(`${tabDetails.type.toLowerCase()}/${tabDetails.subType.toLowerCase()}/${tabDetails.id}`,
          { skipLocationChange: environment.hideUrl });
      }
    } else {
      if (tab.id === this.lastSelectTab.id) {
        this.lastSelectTab = (this.filteredTabs.indexOf(tab) === 0)
          ? this.filteredTabs[this.filteredTabs.indexOf(tab) + 1] : this.filteredTabs[this.filteredTabs.indexOf(tab) - 1];
        this.lmsTabChange.emit(<LmsEventDetails>({ type: 'lastSelectTabChange', data: this.lastSelectTab }));
      }
    }

    const isCurrentSelectedMenuItem = (this.selectedTab && this.selectedTab.type === this.menuItem.type);

    if ((!isCurrentSelectedMenuItem && this.tabs.length > 0) || ((isCurrentSelectedMenuItem && this.filteredTabs.length > 0))) {
      this.lmsTabChange.emit(<LmsEventDetails>({ type: this.configService.getConfigData().tab.event.remove, data: tab }));
      $event.stopPropagation();
    } else {
      this.lmsTabChange.emit(<LmsEventDetails>({ type: this.configService.getConfigData().tab.event.remove, data: tab }));
    }
  }

  /**
   * set current selected tab. lmsTabChange event emits when tab is selected.
   * @param {Tab} tab
   * @param {any} $event
   * @memberOf LmsSubmenuItemComponent
   */
  selectTab(tab: Tab, $event) {
    $event.preventDefault();
    this.router.navigateByUrl(`${tab.type.toLowerCase()}/${tab.subType.toLowerCase()}/${tab.id}`,
      { skipLocationChange: environment.hideUrl });
    this.hideSideNavSubMenuPanel();
  }

  public setEnterButtonFocus() {
    this.popupServices.setSubmitBtnId('navpanelOverlayBtn' + this.menuItem.type);
  }

  public resetEnterButtonFocus() {
    this.popupServices.setSubmitBtnId('');
  }

  /**
   * Destroy the state of lifecycle hook.
   * @memberOf LmsSubmenuItemComponent
   */
  ngOnDestroy() { }

  /**
   * Convert Terminal Milepost to Terminal Mnemonic
   * @param {string} value
   * @returns {string}
   * @memberOf LmsSubmenuItemComponent
   */
  private getConvertedValue(value: string): string {
    let emitValue = value;
    const commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (commonopsPicklist) {
      const data = commonopsPicklist.mapOfMnemonis ? commonopsPicklist.mapOfMnemonis : null;
      if (data) {
        if (data[value]) {
          emitValue = data[value];
        }
      }
    }
    return emitValue;
  }

  /**
   * Notify from autocomplete selection.
   * @memberOf LmsSubmenuItemComponent
   */
  onAutoCompleteNotify() {
    this.addTab();
  }

  addNewTab(event) {
    if (event && event.value) {
      this.addTab();
    }
  }

  /**
   * To check for numeric value.
   * @private
   * @param {string} value
   * @returns {boolean}
   * @memberOf LmsSubmenuItemComponent
   */
  private isNumeric(value: string): boolean {
    const v = value.match(/^\d+$/);
    if (v) {
      if (v.toString() === value) {
        return true;
      }
    }
    return false;
  }

  /**
   * Convert string into standard CSX Loco Id format
   * @private
   * @param {string} value
   * @memberOf LmsSubmenuItemComponent
   */
  private formatLocomotiveId(value: string) {
    let formattedValue = '';
    if (value) {
      value = value.trim();
      if (this.isNumeric(value.charAt(0))) {
        formattedValue = this.defaultLocoInitial + ' ' + value.replace(/^0+/, '');
      } else {
        const v = value.match(/\d+/);
        if (v) {
          const locoInitial = (v.index <= 4) ? value.substring(0, v.index).trim() : value.substring(0, 4).trim();
          const locoNumber = (v.index <= 4) ?
            value.substring(v.index, value.length).trim() : value.substring(4, value.length).trim();
          if (this.isNumeric(locoNumber)) {
            formattedValue = locoInitial + ' ' + locoNumber.replace(/^0+/, '');
          }
        }
      }
    }
    return formattedValue;
  }

  /**
   * Update Search Text
   * @param {string} event
   * @memberOf LmsSubmenuItemComponent
   */
  updateSearchText(event: string) {
    this.searchText = event ? event.toUpperCase() : event;
  }

  /**
   * Invoked when change value from corridor dropdown.
   * @memberof YardLocalHeaderComponent
   */
  changeDivision() {
    this.stationList = [];
    this.stationList.push({
      label: 'All Station',
      value: 'all'
    });
    if (this.selectedDivisionName) {
      if (this.stationList && this.stationList.length > 0) {
        this.selectedStationName = this.stationList[0].value;
      }
      const stationArr = <Array<AllocationStation>>_.filter(this.commonopsPicklist.masterData.allocationStationList,
        ['divisionMnemonic', this.selectedDivisionName]);
      if (stationArr) {
        for (const allocationStation of stationArr) {
          this.stationList.push({
            label: allocationStation.allocationStation,
            value: allocationStation.allocationStation
          });
        }
      }
      this.addTab(true);
    }
  }

  /**
   * Invoked when focus on division list.
   * @memberof YardLocalHeaderComponent
   */
  divisionListFocus() {
    if (!this.divisionList || (this.divisionList && this.divisionList.length <= 1)) {
      this.prepareYardLocalDropdownList();
    }
  }

  /**
 * Invoked when focus on division list.
 * @memberof YardLocalHeaderComponent
 */
  stationListFocus() {
    if (!this.stationList || (this.stationList && this.stationList.length <= 1)) {
      this.stationList = [];
      this.stationList.push({
        label: 'All Station',
        value: 'all'
      });

      if (this.selectedDivisionName) {
        const stationArr = <Array<AllocationStation>>_.filter(this.commonopsPicklist.masterData.allocationStationList,
          ['divisionMnemonic', this.selectedDivisionName]);
        if (stationArr) {
          for (const allocationStation of stationArr) {
            this.stationList.push({
              label: allocationStation.allocationStation,
              value: allocationStation.allocationStation
            });
          }
        }
      }
    }
  }

  prepareYardLocalDropdownList() {
    this.divisionList = [];
    this.stationList = [];
    this.divisionList.push({
      label: 'Select Operating Zone',
      value: ''
    });
    this.stationList.push({
      label: 'All Station',
      value: 'all'
    });

    this.commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (this.commonopsPicklist && this.commonopsPicklist.allocationZoneList) {	
      const zoneArr: Array<any> = this.commonopsPicklist.allocationZoneList;	
      if (zoneArr) {	
        for (const zone of zoneArr) {	
          this.divisionList.push({	
            label: zone.oprZoneCode + ' ' + zone.oprZoneName,	
            value: zone.oprZoneCode	
          });	
        }	
      }	
      if (this.commonopsPicklist.masterData.allocationStationList) {	
        this.allocationStationArr = this.commonopsPicklist.masterData.allocationStationList;	
      }
    }

    this.stationList = [];
    this.stationList.push({
      label: 'All Station',
      value: 'all'
    });

    if (this.selectedDivisionName) {
      let stationArr = this.commonopsPicklist.allocationZoneList	
      .find(obj=> obj.oprZoneCode==this.selectedDivisionName);	
      if (stationArr) {	
        stationArr = stationArr.allocatedStationList.sort();		
        	
        for (let allocationStation of stationArr) {	
          this.stationList.push({	
            label: allocationStation,	
            value: allocationStation
          });
        }
      }
    }
  }
}
